import React, { useState } from 'react';
import { Button, Input, Modal, Tooltip, message } from 'antd';
import { withApollo } from 'react-apollo';
import { SET_MESSAGE_SILENT, SET_COMMENT_SILENT } from '../../api/moderation';
import './index.css';

export function SilentStatus() {
  return <span className="silent-status">已静默删除</span>;
}

export function SilentImpact({ restore, report }) {
  return (
    <div className="silent-impact">
      <strong>{restore ? '公开范围' : '可见范围'}</strong>
      <p>{restore
        ? '其他用户可重新查看内容。若上级帖子或评论仍处于静默删除状态，该分支仍然隐藏。'
        : '作者仍能看到内容，其他用户不可见。'}</p>
      {!restore && <p>作者不会收到通知，信誉积分和举报奖励均不变。{report && '此操作会将举报标记为已处理。'}</p>}
    </div>
  );
}

const formatTime = value => {
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date) : String(value || '—');
};

export function SilentHistory({ item }) {
  const history = item.moderationHistory || [];
  if (!item.silentDeleted && !item.silentModerationAt && !history.length) return null;
  return (
    <aside className="silent-audit" aria-label="静默处理记录">
      {item.silentDeleted && <SilentStatus />}
      {item.silentModerationAt && (
        <div className="silent-audit-meta">
          最近处理 · {item.silentModerationBy || '—'} · {formatTime(item.silentModerationAt)}
          {item.silentModerationReason && <div className="silent-audit-reason">{item.silentModerationReason}</div>}
        </div>
      )}
      {!!history.length && (
        <details>
          <summary>处理记录（{history.length}）</summary>
          <ul>{history.map((entry, index) => (
            <li key={index}>
              <strong>{entry.action === 'restore' ? '恢复公开' : '静默删除'}</strong>
              <span> · {entry.actor || '—'} · {formatTime(entry.at)}</span>
              {entry.reason && <div className="silent-audit-reason">{entry.reason}</div>}
            </li>
          ))}</ul>
        </details>
      )}
    </aside>
  );
}

// Shared by feed, details and batch operations. Keep confirmation open on failure.
function SilentModeration({
  client,
  ids,
  type = 'message',
  silentDeleted = false,
  deleted,
  onDone,
  iconOnly = false,
  className = '',
}) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState('');
  if (deleted || !ids || !ids.length) return null;
  const label = silentDeleted ? '恢复公开' : '静默删除';
  const subject = type === 'comment' ? '评论' : '纸条';

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setFailure('');
    try {
      // Sequential requests avoid duplicate clicks and report partial batch failure.
      for (const _id of ids) {
        await client.mutate({
          mutation:
            type === 'message' ? SET_MESSAGE_SILENT : SET_COMMENT_SILENT,
          variables: { _id, silentDeleted: !silentDeleted, reason },
        });
      }
      if (onDone) onDone();
      setVisible(false);
      setReason('');
      message.success(`${label}成功`);
    } catch (err) {
      setFailure(
        `${err.message || '操作失败'}${
          ids.length > 1 ? '；批量操作可能已部分完成，可刷新后重试' : ''
        }`
      );
    } finally {
      setBusy(false);
    }
  };
  const trigger = (
    <Button
      size={iconOnly ? 'default' : 'small'}
      shape={iconOnly ? 'circle' : undefined}
      icon={silentDeleted ? 'rollback' : 'eye-invisible'}
      aria-label={label}
      onClick={() => {
        setFailure('');
        setVisible(true);
      }}
    >
      {iconOnly ? null : `${label}${ids.length > 1 ? ` (${ids.length})` : ''}`}
    </Button>
  );

  return (
    <span className={`silent-action ${className}`} onClick={event => event.stopPropagation()}>
      {iconOnly ? <Tooltip title={label} trigger={['hover', 'focus']}>{trigger}</Tooltip> : trigger}
      <Modal
        title={`${label}${subject}${ids.length > 1 ? `（${ids.length} 条）` : ''}`}
        visible={visible}
        confirmLoading={busy}
        okText={`确认${label}`}
        okType={silentDeleted ? 'primary' : 'danger'}
        cancelText="取消"
        onOk={submit}
        onCancel={() => !busy && setVisible(false)}
        maskClosable={!busy}
      >
        <SilentImpact restore={silentDeleted} />
        <label className="silent-reason">
          <span>处理原因 <small>选填，仅管理端可见</small></span>
          <Input.TextArea
            rows={3}
            disabled={busy}
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder="填写本次处理的原因"
          />
        </label>
        {failure && <p className="silent-error" role="alert">{failure}</p>}
      </Modal>
    </span>
  );
}
export default withApollo(SilentModeration);
